import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AiOutlineDownload, AiOutlineEye, AiOutlineClose } from 'react-icons/ai';
import { Box } from '@mui/material';
import PocketBase from "pocketbase";

const pb = new PocketBase(process.env.REACT_APP_POCKETBASE_URL);

const Modal = ({ isOpen, onClose, previewUrl }) => {
  if (!isOpen) return null;
  return (
    <StyledModal>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <AiOutlineClose size={24} />
        </CloseButton>
        <PreviewIframe src={previewUrl} title="Note Preview" allow="autoplay" />
      </ModalContent>
    </StyledModal>
  );
};

const NoteItem = ({ name, url, onPreview, onDownload }) => (
  <StyledNoteItem>
    <NoteTitle>{name || 'Untitled Note'}</NoteTitle>
    <Icons>
      <IconButton onClick={() => onPreview(url)}>
        <AiOutlineEye size={20} />
      </IconButton>
      <IconButton onClick={() => onDownload(url)}>
        <AiOutlineDownload size={20} />
      </IconButton>
    </Icons>
  </StyledNoteItem>
);

const IconButton = styled.div`
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const NotesPage = ({ teacherId , noteUrl }) => {
  const [notes, setNotes] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await pb.collection('Class_Notes').getFullList();
        const filteredNotes = response.filter(note => note.teacher === teacherId);
        setNotes(filteredNotes);
      } catch (error) {
        setError('Failed to load notes. Displaying sample data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotes();
  }, [teacherId]);

  const handlePreview = async (note) => {
    try {
      const filePreviewUrl = `${process.env.REACT_APP_POCKETBASE_URL}/api/files/${note.collectionId}/${note.id}/${note.file}`;
      setPreviewUrl(filePreviewUrl);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching file preview:', error);
      alert('Failed to fetch file preview');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPreviewUrl('');
  };

  // const extractFileId = (url) => {
  //   try {
  //     return url.match(/\/d\/(.+?)\//)?.[1] || '';
  //   } catch (error) {
  //     console.error('Invalid URL format:', error);
  //     return '';
  //   }
  // };

  const handleDownload = async (note) => {
    try {

      const fileUrl = `${process.env.REACT_APP_POCKETBASE_URL}/api/files/${note.collectionId}/${note.id}/${note.file}`;
      
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('target', '_blank'); 
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  return (
    <Container>
      <Box sx={{ 
        width: '100%',
        height: '100px',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <img 
          src={noteUrl}
          alt="" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '2%',
          }} 
        />
      </Box>
      <SearchBar
        type="text"
        placeholder="Search notes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {isLoading ? (
        <Message>Loading notes...</Message>
      ) : (
        <>
          {error && <Message>{error}</Message>}

          <NotesList>
            {notes
              .filter(note => 
                note.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .slice(0, 5)
              .map((note) => (
                <NoteItem 
                  key={note.id} 
                  name={note.name} 
                  url={note.url} 
                  onPreview={() => handlePreview(note)} 
                  onDownload={() => handleDownload(note)} 
                />
              ))}
          </NotesList>
        </>
      )}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          previewUrl={previewUrl}
        />
      )}
    </Container>
  );
};

export default NotesPage;

const Container = styled.div`
  padding: 10px;
  max-width: 600px;
  margin: 0 auto;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledNoteItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border: 2px solid #eee;
  border-radius: 12px;
  background: #fff;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const NoteTitle = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const Icons = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  height: 80%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const Message = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 18px;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 8px 12px;
  margin: 10px 0;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2196f3;
    outline: none;
  }
`;